package com.swetdot.swetgram.ui.chat

import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.count
import com.swetdot.swetgram.ui.text.text
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.chat_typing_one
import swetgram.shared.generated.resources.chat_typing_others
import swetgram.shared.generated.resources.chat_typing_two

/**
 * How long one `typing` signal keeps somebody in the indicator.
 *
 * A TTL is not a nicety here, it is the only thing that can end the state. The socket carries
 * `typing` as an EPHEMERAL event (STA §12.5: "Ephemeral events can be published without
 * persistence"), so the `is_typing: false` that should end it is exactly the frame most likely
 * to be dropped -- and it is never sent at all when the other person's app is killed, loses
 * signal, or is force-quit mid-word. Without an expiry the indicator would sit there claiming
 * someone is typing for the rest of the session.
 */
const val TYPING_TTL_MS: Long = 6_000L

/**
 * How often the LOCAL user should re-publish `is_typing: true` while still typing.
 *
 * Comfortably inside [TYPING_TTL_MS], so one dropped frame does not make the indicator flicker
 * off on the other end, and far enough apart that holding a key down does not turn into a
 * request per keystroke against the 60/minute budget in DECISIONS §12.
 */
const val TYPING_REPUBLISH_MS: Long = 3_000L

/**
 * Who is typing in one conversation.
 *
 * Immutable and pure -- no clock, no coroutines, no platform. Every decision it makes is a
 * function of the signals fed in and the `nowMs` asked about, which is what lets it be reasoned
 * about (and tested) without a socket. The owning view model holds one of these per open
 * conversation and re-reads [typistsAt] on a timer, because a state that expires on its own has
 * to be asked, not awaited.
 *
 * It stores USER IDS and nothing else, which is the whole fact the frame carries -- see
 * `TypingEvent`. Resolving an id to a display name is deliberately somebody else's job, done at
 * render time: the socket can deliver a typing frame BEFORE the conversation's member list has
 * been read over REST (a cold open is exactly that race), and a state that stored names would
 * have to drop that frame permanently for want of a string it could look up a moment later.
 *
 * Never feed it the signed-in user's own id. The server publishes to the whole conversation
 * channel including the sender, and "You are typing…" is not a thing to show anybody.
 */
class TypingState private constructor(private val seenAtMs: Map<String, Long>) {

    /**
     * The state after one `typing` signal.
     *
     * Re-signalling somebody already present refreshes their timestamp WITHOUT moving them: the
     * map keeps insertion order, so a person who keeps typing does not jump to the end of the
     * list and rewrite the sentence under the reader's eye every three seconds.
     *
     * Returns THIS when nothing changed. The view model holds this in a `MutableStateFlow`, which
     * conflates by equality -- and since this type has no `equals`, that is identity. Handing back
     * the same instance is what stops a redundant signal from restarting the expiry ticker.
     */
    fun onTyping(userId: String, isTyping: Boolean, atMs: Long): TypingState {
        if (!isTyping) {
            if (userId !in seenAtMs) return this
            return TypingState(seenAtMs - userId)
        }
        if (seenAtMs[userId] == atMs) return this
        return TypingState(seenAtMs + (userId to atMs))
    }

    /**
     * The user ids still within [ttlMs] of their last signal, in the order they STARTED typing --
     * which is why [onTyping] refreshes in place rather than re-inserting.
     *
     * A read, not a sweep: this does not drop the expired entries, because doing so would make
     * a plain query mutate the object and two calls with different clocks disagree about what
     * the state IS. Entries are replaced when the person types again and dropped when they stop.
     */
    fun typistsAt(nowMs: Long, ttlMs: Long = TYPING_TTL_MS): List<String> =
        seenAtMs.entries.filter { nowMs - it.value < ttlMs }.map { it.key }

    companion object {
        val Empty = TypingState(emptyMap())
    }
}

/**
 * What the indicator says, or null when nobody is typing.
 *
 * SDS §18 asks for simple, direct, human copy, so this names people rather than counting them
 * where it can: "Ada is typing…" tells the reader something, "1 person is typing…" tells them
 * arithmetic. Past two the names stop fitting on the one line the header gives them, so the third
 * and beyond collapse into a count.
 *
 * WHERE IT GOES: the conversation header's subtitle, and nowhere else. SDS §12.3 gives that header
 * exactly one status line -- the "Online / last seen summary" -- and typing is what temporarily
 * replaces it; the SDS specifies no separate typing row anywhere on the screen, and the same
 * sentence rendered twice at once reads as a bug rather than as emphasis. `ChatScreen` does the
 * substitution.
 *
 * THERE ARE NO ANIMATED DOTS, and that is a decision rather than an omission. SDS §19.4 lists
 * "Decorative loops" under what motion must NOT be used for, and an indicator that pulses for as
 * long as somebody keeps typing is the definition of one -- `SwetSkeleton` already refused a
 * shimmer on the same clause. The information is carried by the sentence.
 *
 * The ellipsis is U+2026, matching
 * [singleLinePreview][com.swetdot.swetgram.ui.design.singleLinePreview]; three periods would
 * measure differently and read as a different glyph at `labelMedium`. It lives in the resource
 * values now, once per sentence per language, rather than in this file.
 *
 * THREE WHOLE SENTENCES, NEVER A NAME GLUED TO A VERB. Each branch is one resource with the names as
 * ARGUMENTS, which is the only shape Russian survives: the verb agrees with the number of people
 * (печатает against печатают), so a shared tail concatenated onto a variable head would be wrong for
 * one of the two heads and nothing at the call site could tell which.
 *
 * THE THIRD BRANCH IS A PLURAL OVER THE OTHERS, not over the list. `names.size - 1` is the figure the
 * sentence prints AND the number whose grammar it must obey, and they are the same number here only
 * because the first name is named separately. Russian needs `человек` at 1 and 5, `человека` at 2, and
 * `человек` again at 21, which no `if` in Kotlin may decide -- see [UiText.Plural]. The branch is
 * reachable only from three names up, so the `one` form is unreachable in this app; it is written
 * correctly anyway, because "unreachable today" is a fact about the caller and not about the language.
 */
fun typingLabel(names: List<String>): UiText? = when (names.size) {
    0 -> null
    1 -> Res.string.chat_typing_one.text(names[0])
    2 -> Res.string.chat_typing_two.text(names[0], names[1])
    else -> Res.plurals.chat_typing_others.count(names.size - 1, names[0], names.size - 1)
}
